/*
  LP3 — behavior of the lp3-* landing page sections.
  Custom elements, so they also (re)initialize on their own when a section is re-rendered in the theme editor.
*/

/* Header: adds .is-scrolled once the page is scrolled (desktop header is transparent over the hero until then) */
if (!customElements.get('lp3-header')) {
  customElements.define(
    'lp3-header',
    class extends HTMLElement {
      connectedCallback() {
        this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 50);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
      }
    }
  );
}

/*
  Horizontal scroller: native scroll + snap, optional prev / next buttons and drag-to-scroll with the mouse.
    [data-lp3-track]  the scrolling list
    [data-lp3-prev]   [data-lp3-next]  optional buttons
    [data-lp3-progress]  optional progress bar (its thumb reads --lp3-thumb-size / --lp3-thumb-offset)
*/
if (!customElements.get('lp3-scroller')) {
  customElements.define(
    'lp3-scroller',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-lp3-track]');
        if (!this.track) return;

        this.prevButton = this.querySelector('[data-lp3-prev]');
        this.nextButton = this.querySelector('[data-lp3-next]');
        this.progress = this.querySelector('[data-lp3-progress]');
        this.update = this.update.bind(this);

        this.track.addEventListener('scroll', this.update, { passive: true });
        this.prevButton?.addEventListener('click', () => this.scrollByItems(-1));
        this.nextButton?.addEventListener('click', () => this.scrollByItems(1));
        this.setupMouseDrag();

        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
        this.update();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      get maxScroll() {
        return this.track.scrollWidth - this.track.clientWidth;
      }

      scrollByItems(direction) {
        const item = this.track.firstElementChild;
        if (!item) return;

        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.track.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: 'smooth' });
      }

      update() {
        const position = Math.abs(this.track.scrollLeft);

        this.classList.toggle('is-scrollable', this.maxScroll > 1);
        if (this.prevButton) this.prevButton.disabled = position <= 1;
        if (this.nextButton) this.nextButton.disabled = position >= this.maxScroll - 1;

        if (this.progress && this.track.scrollWidth > 0) {
          const visible = this.track.clientWidth / this.track.scrollWidth;
          const scrolled = this.maxScroll > 1 ? position / this.maxScroll : 0;

          this.progress.style.setProperty('--lp3-thumb-size', `${visible * 100}%`);
          this.progress.style.setProperty('--lp3-thumb-offset', `${scrolled * (1 - visible) * 100}%`);
        }
      }

      setupMouseDrag() {
        let startX = 0;
        let startScroll = 0;
        let dragged = false;

        const onMove = (event) => {
          const delta = event.clientX - startX;
          if (!dragged && Math.abs(delta) < 5) return;

          dragged = true;
          this.track.classList.add('is-dragging');
          this.track.scrollLeft = startScroll - delta;
        };

        const onUp = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          this.track.classList.remove('is-dragging');
        };

        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0 || this.maxScroll <= 1) return;

          startX = event.clientX;
          startScroll = this.track.scrollLeft;
          dragged = false;
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
        });

        /* A drag must not count as a click on a linked card */
        this.track.addEventListener(
          'click',
          (event) => {
            if (!dragged) return;
            event.preventDefault();
            event.stopPropagation();
            dragged = false;
          },
          true
        );
      }
    }
  );
}
